import dayjs from '~/timeInit';

export const DATETIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';
export const PAYLOAD_TIMEZONE = 'Australia/Sydney';

// Form values are Australia/Sydney wall-clock. Lab JSON for Offers Hub / What's New / Spend Points
// stores true UTC instants (`Z`) so the customer site's `new Date(startAt)` schedule checks match.
export const DATETIME_INPUT_FORMATS: Array<string> = [
  DATETIME_LOCAL_FORMAT,
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
  'YYYY-MM-DDTHH:mm:ss[Z]',
];

const NAIVE_DATETIME_LOCAL_FORMATS: Array<string> = [DATETIME_LOCAL_FORMAT, 'YYYY-MM-DDTHH:mm:ss'];

function parseNaiveDateTimeLocal(source: string): dayjs.Dayjs | null {
  for (const format of NAIVE_DATETIME_LOCAL_FORMATS) {
    const parsed = dayjs(source, format, true);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  return null;
}

function looksLikeUtcIso(source: string): boolean {
  return /Z$/i.test(source) || /[+-]\d{2}:\d{2}$/.test(source);
}

/**
 * Existing Lab JSON often stored Sydney wall-clock digits with a `Z` suffix
 * (`00:00:00.000Z` for midnight, `23:59:59.999Z` for 11:59pm). Treat those as
 * Sydney form values instead of converting the fake UTC instant.
 *
 * True UTC exports from this builder use other hours (`13:59Z` for 11:59pm AEST).
 */
function isLegacySydneyWallClockUtc(source: string): boolean {
  if (!/Z$/i.test(source)) return false;

  const parsed = dayjs.utc(source);
  if (!parsed.isValid()) return false;

  const hour = parsed.hour();
  const minute = parsed.minute();
  return (hour === 0 && minute === 0) || (hour === 23 && minute === 59);
}

export function isValidDateTimeInput(value: string): boolean {
  const source = value.trim();
  if (!source) return false;
  if (parseNaiveDateTimeLocal(source)) return true;
  return looksLikeUtcIso(source) && dayjs(source).isValid();
}

export function toDateTimeLocal(value: unknown): string {
  const source = typeof value === 'string' ? value.trim() : '';
  if (!source) return '';

  if (!looksLikeUtcIso(source) && parseNaiveDateTimeLocal(source)) {
    return dayjs(source, NAIVE_DATETIME_LOCAL_FORMATS, true).format(DATETIME_LOCAL_FORMAT);
  }

  if (looksLikeUtcIso(source)) {
    const parsed = dayjs.utc(source);
    if (parsed.isValid()) {
      if (isLegacySydneyWallClockUtc(source)) {
        return parsed.format(DATETIME_LOCAL_FORMAT);
      }

      return parsed.tz(PAYLOAD_TIMEZONE).format(DATETIME_LOCAL_FORMAT);
    }
  }

  return source;
}

/** Form Sydney wall-clock (or loaded UTC ISO) → UTC ISO for Lab payloads. */
export function toPayloadIsoDateTime(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const local = toDateTimeLocal(trimmed);
  if (!dayjs(local, DATETIME_LOCAL_FORMAT, true).isValid()) {
    return trimmed;
  }

  try {
    const parsed = dayjs.tz(local, DATETIME_LOCAL_FORMAT, PAYLOAD_TIMEZONE);
    return parsed.isValid() ? parsed.toISOString() : trimmed;
  } catch {
    return trimmed;
  }
}
